/**
 * Which programs offer a form (TRUS-762).
 *
 * The forms hub, pinned forms and Add → Form all list a program's forms from
 * GET /forms/by-program/{id}: the forms linked to that program, plus forms
 * linked to no program at all (offered everywhere). The link API existed but
 * nothing in the builder used it, so admins could not put a form in a program.
 *
 * Ticking a program links the form to it; unticking removes the link (the
 * server also clears a legacy form_templates.program_id). "Required" puts the
 * form first in the hub's Start list.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { formsAPI, type FormProgramAssociation } from '@/services/api/forms';
import { programsAPI, type Program } from '@/services/api/programs';

const errorText = (e: any, fallback: string) =>
  (typeof e?.response?.data?.detail === 'string' && e.response.data.detail) || fallback;

export interface FormTemplateProgramsProps {
  templateId: number;
}

export const FormTemplatePrograms: React.FC<FormTemplateProgramsProps> = ({ templateId }) => {
  const { t } = useAppTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [programs, setPrograms] = useState<Program[]>([]);
  const [links, setLinks] = useState<FormProgramAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [progs, assoc] = await Promise.all([
        programsAPI.listPrograms({ is_active: true }),
        formsAPI.getFormTemplatePrograms(templateId),
      ]);
      setPrograms(Array.isArray(progs) ? progs : []);
      setLinks(Array.isArray(assoc) ? assoc : []);
    } catch (e: any) {
      setError(errorText(e, tRef.current('forms.programs_load_failed', "Could not load this form's programs.")));
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const linkFor = (programId: number) => links.find((l) => l.program_id === programId);

  const change = async (programId: number, fn: () => Promise<unknown>) => {
    setBusyId(programId);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(errorText(e, tRef.current('forms.programs_save_failed', 'Could not change the programs for this form.')));
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (programId: number, on: boolean) =>
    change(programId, () =>
      on
        ? formsAPI.addFormTemplateProgram(templateId, { program_id: programId, is_required: false })
        : formsAPI.removeFormTemplateProgram(templateId, programId),
    );

  const setRequired = (programId: number, required: boolean) =>
    change(programId, () =>
      formsAPI.addFormTemplateProgram(templateId, {
        program_id: programId,
        is_required: required,
        display_order: linkFor(programId)?.display_order ?? 0,
      }),
    );

  return (
    <div className="space-y-2" data-testid="form-template-programs">
      <Label>{t('forms.programs', 'Programs')}</Label>
      <p className="text-xs text-ink-500">
        {links.length === 0
          ? t(
              'forms.programs_none_hint',
              'Not linked to a program, so it is offered in every program. Tick programs to offer it only there.',
            )
          : t('forms.programs_hint', 'Offered only in the ticked programs. Required forms are listed first when staff start a form.')}
      </p>
      {error && (
        <div className="text-sm text-red-600" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-sm text-ink-500">{t('forms.programs_loading', 'Loading programs…')}</p>
      ) : programs.length === 0 ? (
        <p className="text-sm text-ink-500">{t('forms.programs_empty', 'This organization has no active programs.')}</p>
      ) : (
        <ul className="divide-y divide-v2border rounded-md border border-v2border">
          {programs.map((p) => {
            const link = linkFor(p.id);
            const busy = busyId === p.id;
            return (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`form-program-${p.id}`}>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <Checkbox
                    checked={!!link}
                    disabled={busy}
                    aria-label={p.name}
                    onCheckedChange={(v) => void toggle(p.id, v === true)}
                  />
                  {p.name}
                </label>
                {link && (
                  <label className="flex items-center gap-2 text-xs text-ink-500 cursor-pointer">
                    <Checkbox
                      checked={!!link.is_required}
                      disabled={busy}
                      aria-label={t('forms.program_required_for', 'Required in {{name}}', { name: p.name })}
                      onCheckedChange={(v) => void setRequired(p.id, v === true)}
                    />
                    {t('forms.program_required', 'Required')}
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FormTemplatePrograms;
